// PoolScreener data — Binance public client (CORS-friendly, no key):
//   • daily klines  → realized-vol closes + daily quote-volume series
//   • exchangeInfo  → is the volatile asset CEX-listed (flow-toxicity proxy)
//   • fundingRate   → 30d perp funding series (hedge carry)
// Spot endpoints on api.binance.com; funding on fapi.binance.com.
import { z } from 'zod';
import { fetchJson } from './http';
import { cached } from './cache';

// ── klines ── each row: [openTime, open, high, low, close, volume, closeTime,
//    quoteAssetVolume, trades, takerBase, takerQuote, ignore]
export const KlinesResponseSchema = z.array(z.array(z.union([z.number(), z.string()])));
export type KlinesResponse = z.infer<typeof KlinesResponseSchema>;

/** Pure: close price series (index 4). */
export function klineCloses(rows: KlinesResponse): number[] {
  return rows.map((r) => Number(r[4])).filter((n) => Number.isFinite(n));
}
/** Pure: daily quote-asset-volume series (index 7). */
export function klineQuoteVolumes(rows: KlinesResponse): number[] {
  return rows.map((r) => Number(r[7])).filter((n) => Number.isFinite(n));
}

export async function fetchDailyKlines(
  symbol: string,
  days = 30,
): Promise<{ closes: number[]; quoteVolumes: number[] }> {
  const rows = await cached(`binance:klines:${symbol}:${days}`, () =>
    fetchJson(
      `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=1d&limit=${days}`,
      KlinesResponseSchema,
    ),
  );
  return { closes: klineCloses(rows), quoteVolumes: klineQuoteVolumes(rows) };
}

// ── exchangeInfo ──
export const ExchangeInfoSchema = z.object({
  symbols: z.array(
    z.object({ symbol: z.string(), status: z.string(), baseAsset: z.string() }),
  ),
});
export type ExchangeInfo = z.infer<typeof ExchangeInfoSchema>;

/** Pure: set of actively-trading symbols. */
export function tradingSymbols(info: ExchangeInfo): Set<string> {
  return new Set(info.symbols.filter((s) => s.status === 'TRADING').map((s) => s.symbol));
}

/** Is the volatile leg listed against USDT/USDC on Binance? */
export function isCexListed(symbols: Set<string>, baseAsset: string): boolean {
  const b = baseAsset.toUpperCase();
  return symbols.has(`${b}USDT`) || symbols.has(`${b}USDC`);
}

export async function fetchBinanceSymbols(): Promise<Set<string>> {
  const info = await cached('binance:exchangeInfo', () =>
    fetchJson('https://api.binance.com/api/v3/exchangeInfo', ExchangeInfoSchema),
  );
  return tradingSymbols(info);
}

// ── funding ──
export const FundingResponseSchema = z.array(
  z.object({ symbol: z.string(), fundingRate: z.string(), fundingTime: z.number() }),
);
export type FundingResponse = z.infer<typeof FundingResponseSchema>;

/** Pure: per-interval funding rates (fractions). */
export function fundingRates(rows: FundingResponse): number[] {
  return rows.map((r) => Number(r.fundingRate)).filter((n) => Number.isFinite(n));
}

/** 30d of perp funding at 8h settlement ≈ 90 intervals. */
export async function fetchFundingRates(baseAsset: string, limit = 90): Promise<number[]> {
  const symbol = `${baseAsset.toUpperCase()}USDT`;
  const rows = await cached(`binance:funding:${symbol}:${limit}`, () =>
    fetchJson(
      `https://fapi.binance.com/fapi/v1/fundingRate?symbol=${symbol}&limit=${limit}`,
      FundingResponseSchema,
    ),
  );
  return fundingRates(rows);
}
